import { ChangeEvent, Dispatch, Ref, SetStateAction, useState } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Plus } from 'lucide-react';
import { Button } from '@curtin-dxp/curtin-ui';
import { useAwmsStore } from '@/store/AwmsStore';
import { SheetType } from '@/store/types';
import { Input, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui';
import { IconComponent } from '@/components/Common';
import { AddUnitBar } from './AddUnitBar';
import { FILTERS } from '@/constants';

interface StaffUnitHeaderProps {
  parentRef: Ref<HTMLDivElement>;
  schoolList: { id: number; name: string }[] | undefined;
  setColumnFilters?: Dispatch<SetStateAction<ColumnFiltersState>>;
}

export const StaffUnitHeader = ({ parentRef, schoolList, setColumnFilters }: StaffUnitHeaderProps) => {
  const [isAddUnitActive, setIsAddUnitActive] = useState<boolean>(false);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const selectedStaff = useAwmsStore((state) => state.selectedStaff);
  const searchFilters = useAwmsStore((state) => state.searchFilters);
  const selectedSchool = searchFilters[FILTERS.SCHOOL];
  const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
  const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);

  const handleRedirect = (activeSheet: string) => {
    setSelectedStaff({
      id: selectedStaff.id,
      firstName: selectedStaff.firstName,
      lastName: selectedStaff.lastName,
    });
    setActiveSheet(activeSheet as SheetType);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setColumnFilters?.((prev) => [...prev.filter((f) => f.id !== 'unit'), ...(value ? [{ id: 'unit', value }] : [])]);
  };

  return (
    <SheetHeader className="m-0 flex w-full flex-col gap-0 space-y-0">
      <div className="m-0 flex h-[112px] w-full flex-row items-center justify-between self-stretch bg-grey-100 px-8 py-[20px] pr-14">
        <div className="mb-0 mr-2 flex items-center gap-10">
          <div className="flex w-[189] shrink-0 flex-col items-start gap-1">
            <SheetTitle className="text-sh3 font-bold text-primary-black-90">Allocate staff units</SheetTitle>
            <SheetDescription className="text-b2 font-semibold text-primary-black-90">
              {selectedStaff?.lastName?.toString() ?? ''}, {selectedStaff?.firstName?.toString() ?? ''}
            </SheetDescription>
            <Button
              onClick={() => handleRedirect('staff-workload')}
              className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
              variant={null}
            >
              Allocate staff workload
            </Button>
          </div>
          <div className="relative flex w-[260px] items-center">
            <Input
              placeholder="Search unit by id or name"
              className="h-10 w-full bg-white px-3 pl-5 pr-9 text-b2 placeholder:line-clamp-1 placeholder:overflow-hidden placeholder:truncate
                placeholder:text-secondary-grey-01 hover:border-deep-blue focus-visible:ring-deep-blue"
              aria-label="Search unit by id or name"
              onChange={handleSearch}
            />
            <MagnifyingGlassIcon className="absolute right-3 h-[18px] w-[18px]" />
          </div>
          <div>
            <Button
              onClick={() => setIsAddUnitActive(!isAddUnitActive)}
              variant="filled-secondary"
              className="flex h-10 gap-2 rounded-lg text-b2 !font-semibold text-white"
            >
              Add a unit
              <IconComponent className="size-6" IconType={Plus} />
            </Button>
          </div>
        </div>
        <div className="flex shrink items-start justify-center gap-3 text-b3 font-semibold text-secondary-grey-02">
          <span className="ml-1 flex shrink">{selectedSchool?.label?.toString() ?? ''}</span>
          <span className="flex shrink-0 self-center align-middle">{activeYear}</span>
        </div>
      </div>
      {isAddUnitActive && (
        <AddUnitBar
          isVisible={isAddUnitActive}
          isVisibleCallback={setIsAddUnitActive}
          parentRef={parentRef}
          schoolList={schoolList}
        />
      )}
    </SheetHeader>
  );
};
